import { onActivated, ref, type ComponentPublicInstance } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { unrefElement } from '@vueuse/core';

/**
 * Keeps a scroll container's offset across a `<keep-alive>` round trip, so a
 * list view the user drilled into comes back exactly where they left it.
 *
 * KeepAlive alone can't do this. Its `deactivate` parks the subtree in an
 * off-document `<div>`, and an element that isn't rendered has no scroll offset
 * — the browser zeroes it, and reattaching does not bring it back. Component
 * state (filters, sort order) survives on its own; only the DOM offset needs
 * carrying, which is all this does.
 *
 * `onBeforeRouteLeave` is the save point rather than `onDeactivated`, because
 * that move happens *before* the `da` hooks run — by the time `onDeactivated`
 * fires the offset already reads 0.
 *
 * Bind the returned ref with `ref="…"` on the scrolling element, or on a
 * component whose root is one: `UTable` exposes its `relative overflow-auto`
 * root as `$el`, so `<UTable ref="tableScroller">` works directly.
 */
export function useSaveScrollPosition() {
  const target = ref<HTMLElement | ComponentPublicInstance | null>(null);
  let scrollTop = 0;
  let scrollLeft = 0;

  onBeforeRouteLeave(() => {
    const el = unrefElement(target);
    if (!el) return;
    scrollTop = el.scrollTop;
    scrollLeft = el.scrollLeft;
  });

  // Only fires on reactivation, never on the first mount — so there is no
  // initial run to guard against.
  onActivated(() => {
    const el = unrefElement(target);
    if (!el) return;
    el.scrollTop = scrollTop;
    el.scrollLeft = scrollLeft;
  });

  return target;
}
